import React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {useForm, FormProvider, SubmitHandler} from 'react-hook-form';
import {toast} from 'react-toastify';
import isEmail from '../../helpers/isEmail';

import {auth} from '../../firebase';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from 'firebase/auth';

interface SignUpDialogProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface SignUpFormData {
    email: string;
    password: string;
    confirmPassword: string;
  }

const SignUpDialog = (props: SignUpDialogProps) => {
  const {
    open,
    setOpen,
  } = props;

  const methods = useForm<SignUpFormData>({
    mode: 'onChange',
  });

  const {
    reset,
    register,
    watch,
    formState: {isSubmitting, errors, isValid},
  } = methods;

  const [email] = watch(['email']);

  const onSubmit: SubmitHandler<SignUpFormData> = (data) => {
    const {
      email,
      password,
      confirmPassword,
    } = data;

    if (password !== confirmPassword) {
      toast.error('As duas senhas precisam ser iguais!', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return;
    }

    createUserWithEmailAndPassword(auth, email, password)
        .then((userRecord) => {
          const successString = 'Cadastro concluído! ' +
          'Confirme seu e-mail para acessar.';

          toast.success(successString, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });

          return sendEmailVerification(userRecord.user);
        })
        .catch((e) => {
          if (e?.code === 'auth/user-not-found') {
            toast.error('Usuário não encontrado!', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });

            reset({
              email: '',
              password: '',
            });
          }
        });
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Cadastro</DialogTitle>
        <DialogContent>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>

              <TextField
                margin="normal"
                required
                fullWidth
                {
                  ...register('email', {
                    required: true,
                  })
                }
                label="E-mail"
                autoComplete="email"
                autoFocus
                error={
                  (
                    errors?.email || (
                      email && (
                        !isEmail(email) ||
                        !email.endsWith('@drtis.com.br')
                      )
                    )
                    ) ?
                  true :
                  false
                }
                helperText={<>
                  {
                    errors?.password?.type === 'required' &&
                  'Obrigatório preencher.'
                  }
                  {
                    email &&
                  !isEmail(email) &&
                  'Digite um e-mail válido.'
                  }
                  {
                    email &&
                    !email.endsWith('@drtis.com.br') &&
                    'Apenas e-mails @drtis.com.br podem acessar o sistema.'
                  }
                </>}
                disabled={isSubmitting}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                {
                  ...register('password', {
                    required: true,
                    minLength: 6,
                  })
                }
                label="Senha"
                type="password"
                autoComplete="current-password"
                error={errors?.password ? true : false}
                helperText={<>
                  {errors?.password?.type === 'required' &&
                  'Obrigatório preencher.'}
                  {errors?.password?.type === 'minLength' &&
                  'Digite pelo menos 6 caracteres.'}
                </>}
                disabled={isSubmitting}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                {
                  ...register('confirmPassword', {
                    required: true,
                    minLength: 6,
                  })
                }
                label="Confirme a senha"
                type="password"
                error={errors?.confirmPassword ? true : false}
                helperText={<>
                  {errors?.confirmPassword?.type === 'required' &&
                  'Obrigatório preencher.'}
                  {errors?.confirmPassword?.type === 'minLength' &&
                  'Digite pelo menos 6 caracteres.'}
                </>}
                disabled={isSubmitting}
              />
              <DialogActions>
                <Button onClick={handleClose}>Fechar</Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{mt: 3, mb: 2}}
                  disabled={isSubmitting || !isValid}
                >
                Cadastrar
                </Button>
              </DialogActions>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SignUpDialog;
